import { forwardRef } from 'react';
import styled from 'styled-components';

import Image from 'next/image';
import Link from 'next/link';
import { SOLUTIONS_ITEMS_V2 } from 'shared/components/NavBar-v2/SubMenuItems';
import useTranslation from 'shared/hooks/useTranslation';

const Box = styled.div`
  cursor: pointer;
  background: ${({ theme }) => theme.COLORS.V2.PRIMARY};

  img {
    filter: brightness(0) saturate(100%) invert(100%) sepia(1%) saturate(2343%) hue-rotate(217deg) brightness(114%)
      contrast(101%);
  }

  :first-child {
    background: ${({ theme }) => theme.COLORS.V2.SECONDARY};
  }

  :last-child {
    background: ${({ theme }) => theme.COLORS.V2.SECONDARY};
  }

  h4 {
    font-size: 1.1rem;
    font-weight: 700;
    line-height: 1.5rem;
    text-transform: uppercase;
  }

  p {
    font-size: 0.9rem;
    font-weight: 300;
    line-height: 1.1rem;
  }

  @media ${({ theme }) => theme.DEVICE.LG} {
    h4 {
      font-size: 1.8rem;
      font-weight: 700;
      line-height: 2.5rem;
    }

    p {
      font-size: 1.8rem;
      font-weight: 300;
      line-height: 2.5rem;
    }
  }

  h4,
  p {
    color: ${({ theme }) => theme.COLORS.WHITE};
    font-family: ${({ theme }) => theme.FONTS.MAIN};
    text-align: center;
  }
`;

const MoreLink = styled.div`
  height: 3rem;
  width: max-content;
  font-family: inherit;
  font-size: 1rem;
  padding: 1.2rem;
  display: flex;
  color: inherit;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  border-bottom: 1px solid;
  position: relative;
  margin-top: 2.4rem;
  transition: all 0.6s ease 0s;
  color: white;

  @media ${({ theme }) => theme.DEVICE.XL} {
    font-size: 1.4rem;
  }

  span {
    font-weight: 700;
    margin-left: 0.6rem;
  }
`;

const BoxInner = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 1.2rem 2.4rem;
  gap: 1.2rem;
  height: 100%;
  text-decoration: none;

  & > div {
    opacity: 0;
  }

  &:hover > div {
    opacity: 1;
  }
`;

type CardProps = {
  selectedItem: number[];
};

const Card = forwardRef<HTMLDivElement, CardProps>(({ selectedItem }: CardProps) => {
  const { t } = useTranslation('common');

  return (
    <>
      {SOLUTIONS_ITEMS_V2.filter((x) => selectedItem.includes(x.id)).map((data) => (
        <Box key={data.id}>
          <BoxInner href={data.link} passHref>
            <>
              <Image alt="" src={`/img/shared/${data.icon}.svg`} width={43} height={43} suppressHydrationWarning />
              <h4 suppressHydrationWarning>{t(`navbar.${data.name}`)}</h4>
              <p suppressHydrationWarning>{t(`navbar.${data.description}`)}</p>
              <MoreLink>
                {t('button.seeMore')} <span>{'->'}</span>
              </MoreLink>
            </>
          </BoxInner>
        </Box>
      ))}
    </>
  );
});

Card.displayName = 'Card';

export default Card;
